import React, { useEffect, useMemo, useState } from 'react'
import isEqual from 'react-fast-compare'
import { Title, Description } from './ProfileModal.styles'
import SelectableDocumentList from '../../../designSystems/components/SelectableDocumentList'
import {
  GetSharesForDocumentSelectionQueryVariables,
  useGetSharesForDocumentSelectionQuery,
  WorkspaceMinimalFragment,
} from '@sketch/gql-types'
import { useDebounceValue } from '@sketch/utils'
import { Checkbox, handleFetchMore } from '@sketch/components'
import { dataIdFromObject } from '@sketch/graphql-cache'
import {
  ContentContainer,
  ContentContainerUpdateFlow,
  SeparatorCreateFlow,
  SeparatorUpdateFlow,
  Wrapper,
  IconWrapper,
  StatesWrapper,
  StyledFolderClosed,
  FilterContainer,
} from './SelectDocumentsStep.styles'
import { ReactComponent as SearchIcon } from '@sketch/icons/search-64'
import { ReactComponent as WarningTriangle } from '@sketch/icons/warning-triangle-64'
import { FormikProps } from 'formik'
import { initialValues } from './form'

const LOADING_PAGE_SIZE = 3
const ENTRIES_PATH = ['workspace', 'shares', 'entries']

const NoResults = () => (
  <StatesWrapper>
    <IconWrapper>
      <SearchIcon height="64px" />
    </IconWrapper>
    <Title>No documents found</Title>
    <Description>There are no documents matching your search.</Description>
  </StatesWrapper>
)

const ErrorState = () => (
  <StatesWrapper>
    <IconWrapper>
      <WarningTriangle height="64px" />
    </IconWrapper>
    <Title>An error occurred</Title>
    <Description>
      There was an error while searching for documents. Please try again.
    </Description>
  </StatesWrapper>
)

const EmptyState = () => (
  <StatesWrapper>
    <IconWrapper>
      <StyledFolderClosed height="64px" />
    </IconWrapper>
    <Title>No documents</Title>
    <Description>
      Add some documents to choose them for your profile.
    </Description>
  </StatesWrapper>
)

export interface FormProps {
  selectedShares: string[]
  selectAll: boolean
}

interface SelectDocumentsStepProps {
  workspace: WorkspaceMinimalFragment
  formikBag: FormikProps<typeof initialValues>
  isProfileEditRoute?: boolean
}

export const SelectDocumentsStep = ({
  workspace,
  formikBag,
  isProfileEditRoute,
}: SelectDocumentsStepProps) => {
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounceValue(search, 500)
  const [showOnlyPublished, setShowOnlyPublished] = useState(false)

  const sharesQueryVariables = useMemo(() => {
    const filters = ['EXCLUDE_PERSONAL_DRAFTS']
    if (showOnlyPublished) {
      filters.push('PUBLISHED')
    }
    return {
      identifier: workspace.identifier,
      search: {
        name: debouncedSearch,
        filters,
      },
      sortOrder: 'LAST_MODIFIED_DESC',
    }
  }, [workspace.identifier, debouncedSearch, showOnlyPublished])

  const {
    data,
    error,
    loading,
    fetchMore: fetchMoreShares,
  } = useGetSharesForDocumentSelectionQuery({
    variables:
      sharesQueryVariables as GetSharesForDocumentSelectionQueryVariables,
    fetchPolicy: 'cache-and-network',
  })
  const shares = data?.workspace.shares
  const sharesEntries = useMemo(() => shares?.entries || [], [shares])
  const hasPublishedDocuments = useMemo(
    () => sharesEntries.some(share => share.publication),
    [sharesEntries]
  )

  // Set "Select All" checkbox value to true, if all published shares match the
  // one already fetched.
  // Meaning that, if in page 2, we have items not yet published, the checkbox
  // switches to false.
  const allShareIdentifiers = useMemo(
    () => sharesEntries.map(({ identifier }) => identifier),
    [sharesEntries]
  )
  const { setFieldValue } = formikBag
  useEffect(() => {
    setFieldValue(
      'selectAll',
      isEqual(allShareIdentifiers, formikBag.values.shares)
    )
  }, [allShareIdentifiers, formikBag.values.shares, setFieldValue])

  const after = shares?.meta.after
  const totalCount = shares?.meta.totalCount ?? 0

  const remainingCount = totalCount - sharesEntries.length
  const placeholderCount = Math.min(LOADING_PAGE_SIZE, remainingCount)

  const fetchMore = handleFetchMore(fetchMoreShares, ENTRIES_PATH, {
    dataIdFromObject,
    after,
  })

  const searchPlaceholder = `Search documents in "${workspace.name}"`

  const noResults = !sharesEntries.length && debouncedSearch
  const errorState = error ? <ErrorState /> : null
  const noResultsState = noResults ? <NoResults /> : null
  const emptyState =
    !loading && !sharesEntries.length && !debouncedSearch ? (
      <EmptyState />
    ) : null

  const isSearchDisabled = !!emptyState

  const resetSelectedShares = () => {
    formikBag.setFieldValue('shares', [])
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const allShareIds = sharesEntries.map(item => item.identifier)
      formikBag.setFieldValue('shares', allShareIds)
    }
    formikBag.handleChange(e)
  }

  const handleFilterByPublished = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShowOnlyPublished(e.target.checked)
  }

  return (
    <Wrapper>
      {isProfileEditRoute ? (
        <>
          <SeparatorUpdateFlow />
          <ContentContainerUpdateFlow>
            <Description>
              Select the Documents, Libraries, and Templates you want to share
              with the world. These documents will be publicly visible, even if
              your profile is hidden.
            </Description>
          </ContentContainerUpdateFlow>
        </>
      ) : (
        <>
          <ContentContainer>
            <Title>Choose Documents</Title>
            <Description>
              Select the Documents, Libraries, and Templates you want to share
              with the world. These documents will be publicly visible, even if
              your profile is hidden.
            </Description>
          </ContentContainer>
          <SeparatorCreateFlow />
        </>
      )}

      <SelectableDocumentList
        // Data
        name="shares"
        items={sharesEntries}
        selectedItems={formikBag.values.shares}
        enableSelectAll
        selectAll={formikBag.values.selectAll}
        placeholderCount={placeholderCount}
        // Loading
        loading={loading}
        // Handlers
        onItemChange={handleChange}
        fetchMore={fetchMore}
        resetSelectedShares={resetSelectedShares}
        // Search
        search={search}
        searchPlaceholder={searchPlaceholder}
        onSearch={setSearch}
        isSearchDisabled={isSearchDisabled}
        searchFilterComponent={
          isProfileEditRoute ? (
            <FilterContainer>
              <Checkbox
                label="Show Published"
                checked={showOnlyPublished}
                disabled={!hasPublishedDocuments}
                onChange={handleFilterByPublished}
              />
            </FilterContainer>
          ) : undefined
        }
        // UI States
        emptyState={emptyState}
        errorState={errorState}
        noResultsState={noResultsState}
      />
    </Wrapper>
  )
}
